import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  DeleteSweepIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  RestartAltIcon,
  Stack,
  Text,
} from '@billda/design-system';
import { useResetPots } from '../../../features/development/hooks/useResetPots';

export const PotsDebugSection: React.FC = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useResetPots();

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };

  const handleConfirmReset = () => {
    setConfirmOpen(false);
    mutation.mutate();
  };

  // Auto-clear success/error after 8 seconds (longer than jobs since more info to read)
  React.useEffect(() => {
    if (mutation.isSuccess || mutation.isError) {
      const timer = setTimeout(() => {
        mutation.reset();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [mutation.isSuccess, mutation.isError, mutation]);

  return (
    <Stack spacing={3}>
      {/* Header */}
      <div>
        <div className="mb-1 flex items-center gap-2">
          <RestartAltIcon
            aria-hidden="true"
            className="size-5 text-content-accent"
          />
          <Text as="h6" variant="h6">
            Pot Data Reset
          </Text>
        </div>
        <Text variant="body2" color="secondary">
          Reset all pot data for testing pot transitions from scratch
        </Text>
      </div>

      {/* Warning */}
      <Alert severity="warning">
        <Text variant="body2">
          <strong>Destructive action:</strong> This will permanently delete all
          allocations, deductions, pots, periods, and related events for your
          organisation. Time will also be reset to system time.
        </Text>
      </Alert>

      {/* Reset Action */}
      <Card>
        <CardContent className="p-6">
          <Text as="h6" variant="h6" className="mb-2">
            Reset All Pot Data
          </Text>
          <Text variant="body2" color="secondary" className="mb-4">
            Use this after testing pot transitions to clear all data and reset
            time back to the current system time. This allows you to start fresh
            without reseeding the database.
          </Text>

          <div className="flex items-center gap-4">
            <Button
              variant="danger"
              loading={mutation.isPending}
              onClick={handleOpenConfirm}
              disabled={mutation.isPending}
              className="min-w-[200px]"
            >
              {!mutation.isPending && (
                <DeleteSweepIcon aria-hidden="true" className="size-4" />
              )}
              {mutation.isPending ? 'Resetting...' : 'Reset Pot Data'}
            </Button>

            {/* Success Message */}
            {mutation.isSuccess && mutation.data && (
              <Alert severity="success" className="flex-1 py-0.5">
                <Text variant="body2">
                  Reset complete: {mutation.data.deletedAllocations}{' '}
                  allocations, {mutation.data.deletedDeductions} deductions,{' '}
                  {mutation.data.deletedPots} pots,{' '}
                  {mutation.data.deletedPeriods} periods deleted. Time reset to
                  system time.
                </Text>
              </Alert>
            )}

            {/* Error Message */}
            {mutation.isError && (
              <Alert severity="error" className="flex-1 py-0.5">
                {mutation.error instanceof Error
                  ? mutation.error.message
                  : 'Failed to reset pot data'}
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onOpenChange={next => {
          if (!next) {
            handleCloseConfirm();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>Confirm Pot Data Reset</DialogHeader>
          <DialogBody>
            Are you sure you want to reset all pot data? This will delete:
            <ul className="my-2 list-inside list-disc">
              <li>All allocations (booked work)</li>
              <li>All deductions</li>
              <li>All payment pots</li>
              <li>All pot periods</li>
              <li>All related events</li>
            </ul>
            <div className="mt-2">
              Organisation time will be reset to the current system time.
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleCloseConfirm}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmReset} autoFocus>
              Reset All Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};
